import React, { useEffect, useMemo, useRef, useState } from "react";
import { CloseIcon } from "asset/icons";
import Input from "component/form/Input";
import currencyPairCheck from "utilities/currencyPairCheck";

const normalizeCode = (value) =>
    String(value || "")
        .trim()
        .toUpperCase();

const formatNumber = (value, options = {}) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "0";

    return new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 2,
        ...options,
    }).format(numericValue);
};

const sanitizeRate = (value) =>
    String(value || "")
        .replace(/,/g, "")
        .replace(/[^\d.]/g, "")
        .replace(/^(\d*\.\d*).*$/, "$1");

const formatEditableRate = (value) => {
    const rawValue = String(value || "");
    if (!rawValue) return "";

    const [wholeNumber = "", decimalNumber] = rawValue.split(".");
    const formattedWholeNumber = wholeNumber
        ? new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
              Number(wholeNumber)
          )
        : "0";

    return rawValue.includes(".")
        ? `${formattedWholeNumber}.${decimalNumber ?? ""}`
        : formattedWholeNumber;
};

const NegotiateRateModal = ({
    isOpen,
    onClose,
    onApply,
    currentRate,
    amount,
    fromCurrency,
    toCurrency,
    initialPreferredRate = "",
    initialNotes = "",
    walkthroughActive = false,
}) => {
    const panelRef = useRef(null);
    const [preferredRate, setPreferredRate] = useState("");
    const [notes, setNotes] = useState("");

    const from = normalizeCode(fromCurrency);
    const to = normalizeCode(toCurrency);
    const fromSymbol = currencyPairCheck(from);
    const toSymbol = currencyPairCheck(to);
    const numericCurrentRate = Number(currentRate) || 0;
    const numericPreferredRate = Number(preferredRate) || 0;
    const recipientAmount = Number(amount || 0) * numericPreferredRate;
    const hasEditedRate = numericPreferredRate > 0 && numericPreferredRate !== numericCurrentRate;
    const canApply = numericPreferredRate > 0;

    useEffect(() => {
        if (!isOpen) return undefined;

        setPreferredRate(String(initialPreferredRate || numericCurrentRate || ""));
        setNotes(initialNotes || "");

        const handleKeyDown = (event) => {
            if (event.key === "Escape") onClose?.();
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [initialNotes, initialPreferredRate, isOpen, numericCurrentRate, onClose]);

    const formattedPreferredRate = useMemo(
        () => formatEditableRate(preferredRate),
        [preferredRate]
    );

    if (!isOpen) return null;

    const handleBackdropClick = (event) => {
        if (panelRef.current && !panelRef.current.contains(event.target)) onClose?.();
    };

    const handleApply = () => {
        if (!canApply) return;
        onApply?.({ preferredRate: String(numericPreferredRate), notes: notes.trim() });
    };

    const resetRate = () => setPreferredRate(String(numericCurrentRate || ""));

    return (
        <div
            className={`fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-3 sm:p-4 ${
                walkthroughActive ? "min-[1100px]:pr-[424px]" : ""
            }`}
            onMouseDown={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="negotiate-rate-title"
        >
            <div
                ref={panelRef}
                data-feature-tour="otc-negotiate-modal"
                className="relative flex max-h-[calc(100dvh-24px)] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
            >
                <div className="overflow-y-auto !px-5 !py-5 sm:px-7 sm:py-7">
                    <div className="flex items-center justify-between gap-4">
                        <h2
                            id="negotiate-rate-title"
                            className="aku-heading-h2-semibold !mb-0 !text-[#161827]"
                        >
                            Negotiate rate
                        </h2>
                        <button
                            type="button"
                            onClick={onClose}
                            className="inline-flex size-8 items-center justify-center rounded-full"
                            aria-label="Close"
                        >
                            <CloseIcon className="!size-5" />
                        </button>
                    </div>

                    <div className="mt-7 rounded-xl border border-solid !border-[#D9CDFF] bg-[#FBFAFF] px-4 !py-5 text-center">
                        <p className="aku-label-medium !text-[#64748B]">Current rate</p>
                        <p className="mt-1 aku-heading-h3-medium !text-[#161827]">
                            1 {from} = {formatNumber(numericCurrentRate, {
                                maximumFractionDigits: 8,
                            })} {to}
                        </p>
                    </div>

                    <div className="!mt-5">
                        <Input
                            name="negotiated-rate"
                            label="Your preferred rate"
                            labelClassName="aku-body-small-regular !mb-2 !text-[#383B56]"
                            type="text"
                            inputMode="decimal"
                            required={false}
                            value={formattedPreferredRate}
                            onChange={(value) => setPreferredRate(sanitizeRate(value))}
                            prefix={
                                <span className="inline-flex items-center gap-2 whitespace-nowrap aku-body-normal-regular !text-[#383B56]">
                                    <span>{fromSymbol}1 =</span>
                                    <span className="!text-[#64748B]">{toSymbol}</span>
                                </span>
                            }
                            unit={
                                hasEditedRate ? (
                                    <div className="!pr-4">
                                        <button
                                            type="button"
                                            onClick={resetRate}
                                            className="aku-body-small-medium !text-[#6F00FF]"
                                        >
                                            Reset
                                        </button>
                                    </div>
                                ) : null
                            }
                            unitWrapperClassName="!mt-0 !rounded-xl"
                            className="!mt-0 !min-w-0 !px-2 aku-body-normal-regular !text-[#161827]"
                        />
                        {numericPreferredRate > 0 && Number(amount) > 0 && (
                            <p className="mt-2 aku-label-regular !text-[#45556C]">
                                Recipient would receive ~ {toSymbol}
                                {formatNumber(recipientAmount, {
                                    minimumFractionDigits: 2,
                                })}
                            </p>
                        )}
                    </div>

                    <div className="mt-7">
                        <Input
                            name="negotiated-rate-notes"
                            type="textarea"
                            required={false}
                            label={
                                <>
                                    Notes <span className="italic !text-[#45556C]">• Optional</span>
                                </>
                            }
                            labelClassName="aku-body-small-regular !mb-2 !text-[#161827]"
                            value={notes}
                            onChange={setNotes}
                            placeholder="Write a note here"
                            className="!mt-0 !rounded-xl aku-body-small-regular !text-[#161827] placeholder:!text-[#64748B]"
                        />
                    </div>
                </div>

                <div className="flex shrink-0 items-center justify-end gap-3 border-t border-[#E7E9F4] !px-5 py-4 sm:px-7">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2.5 aku-body-small-medium !text-[#383B56]"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        disabled={!canApply}
                        className="rounded-xl bg-[#6F00FF] px-6 py-2.5 aku-body-small-medium !text-white transition hover:bg-[#5A00D6] disabled:cursor-not-allowed disabled:bg-[#D7C8FF]"
                    >
                        Apply rate
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NegotiateRateModal;
